import { useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Typography } from "@mui/material";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { useUpcomingAnniversariesData } from "../../../store/selectors/reportingSelectors";
import { fetchUpcomingAnniversariesThunk } from "../../../store/thunk/reportingThunk";
import { useAppDispatch } from "../../../hooks/StoreHooks";
import { chartColorList } from "../constants";
import { ChartContainer } from "./Widget.styles";
import { chartValueType } from "./GeneralInfo/ChartDateType";

const CustomTooltip = ({ active, payload }: any) => {
  const { t } = useTranslation();
  if (active && payload && payload.length) {
    return (
      <div style={{
        backgroundColor: "white",
        padding: "8px",
        border: "1px solid #ccc",
        borderRadius: "5px",
        boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.1)"
      }}>
        <Typography variant="body2" fontWeight="bold">{payload[0].name}</Typography>
        <Typography>{`${t("dashboardPage.upcomingAnniversaries.anniversaries")}: ${payload[0].value}`}</Typography>
      </div>
    );
  }
  return null;
};

const UpcomingAnniversariesChart = () => {
  const { t } = useTranslation();
  const upcomingAnniversariesData = useUpcomingAnniversariesData();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!upcomingAnniversariesData) {
      dispatch(fetchUpcomingAnniversariesThunk());
    }
  }, [dispatch, upcomingAnniversariesData]);

  const currentMonthLabel = t("dashboardPage.upcomingAnniversaries.numberAnniversariesInCurrentMonth");
  const nextMonthLabel = t("dashboardPage.upcomingAnniversaries.numberAnniversariesInNextMonth");

  const chartData: chartValueType[] = useMemo(() => {
    if (!upcomingAnniversariesData) {
      return [{ name: "", value: 0, fill: "#cccccc" }];
    }
    return [
      {
        name: currentMonthLabel,
        value: upcomingAnniversariesData?.numberAnniversariesInCurrentMonth || 0,
        fill: chartColorList[0],
      },
      {
        name: nextMonthLabel,
        value: upcomingAnniversariesData?.numberAnniversariesInNextMonth || 0,
        fill: chartColorList[1],
      },
    ]
  }, [upcomingAnniversariesData, currentMonthLabel, nextMonthLabel]);

  return (
    <>
      <ChartContainer>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 5 }}>
            <XAxis dataKey="name" />
            <YAxis allowDecimals={false} />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="value" fill={chartColorList[0]} barSize={20} />
          </BarChart>
        </ResponsiveContainer>
      </ChartContainer>
    </>
  );
};

export default UpcomingAnniversariesChart;
